import { useState, useEffect } from 'react';
import { Plus, Car, Edit, Trash2, Search, History } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import api from '@/lib/api';
import { toast } from 'sonner';
import HistoricoVeiculoModal from '@/components/HistoricoVeiculoModal';

function Veiculos() {
  const [veiculos, setVeiculos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingVeiculo, setEditingVeiculo] = useState(null);
  const [historicoVeiculo, setHistoricoVeiculo] = useState(null);
  const [formData, setFormData] = useState({
    cliente_id: '',
    placa: '',
    marca: '',
    modelo: '',
    ano: '',
    cor: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [veiculosRes, clientesRes] = await Promise.all([
        api.get('/veiculos'),
        api.get('/clientes')
      ]);
      setVeiculos(veiculosRes.data);
      setClientes(clientesRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
      console.error(error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = { ...formData, ano: formData.ano ? parseInt(formData.ano) : null };
      if (editingVeiculo) {
        await api.put(`/veiculos/${editingVeiculo.id}`, submitData);
        toast.success('Veículo atualizado com sucesso');
      } else {
        await api.post('/veiculos', submitData);
        toast.success('Veículo cadastrado com sucesso');
      }
      setIsDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error('Erro ao salvar veículo');
      console.error(error);
    }
  };

  const handleEdit = (veiculo) => {
    setEditingVeiculo(veiculo);
    setFormData({
      cliente_id: veiculo.cliente_id,
      placa: veiculo.placa,
      marca: veiculo.marca,
      modelo: veiculo.modelo,
      ano: veiculo.ano ? veiculo.ano.toString() : '',
      cor: veiculo.cor || ''
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja deletar este veículo?')) {
      try {
        await api.delete(`/veiculos/${id}`);
        toast.success('Veículo deletado com sucesso');
        loadData();
      } catch (error) {
        toast.error('Erro ao deletar veículo');
        console.error(error);
      }
    }
  };

  const resetForm = () => {
    setFormData({ cliente_id: '', placa: '', marca: '', modelo: '', ano: '', cor: '' });
    setEditingVeiculo(null);
  };

  const getClienteNome = (cliente_id) => {
    const cliente = clientes.find(c => c.id === cliente_id);
    return cliente ? cliente.nome : 'Cliente não encontrado';
  };

  const filteredVeiculos = veiculos.filter(v =>
    v.placa.toLowerCase().includes(searchTerm.toLowerCase()) ||
    v.marca.toLowerCase().includes(searchTerm.toLowerCase()) ||
    v.modelo.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-8" data-testid="veiculos-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-bold text-white uppercase tracking-tight mb-2">Veículos</h1>
          <p className="text-slate-500">Cadastro de veículos dos clientes</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="bg-electric-blue hover:bg-blue-600 text-white rounded-sm" data-testid="add-veiculo-btn">
              <Plus className="w-4 h-4 mr-2" />
              Novo Veículo
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-carbon border-white/10 text-white max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold uppercase">
                {editingVeiculo ? 'Editar Veículo' : 'Novo Veículo'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400">Cliente</Label>
                <Select value={formData.cliente_id} onValueChange={(value) => setFormData({ ...formData, cliente_id: value })} required>
                  <SelectTrigger className="bg-obsidian border-white/10 text-white">
                    <SelectValue placeholder="Selecione um cliente" />
                  </SelectTrigger>
                  <SelectContent className="bg-carbon border-white/10 text-white">
                    {clientes.map(cliente => (
                      <SelectItem key={cliente.id} value={cliente.id}>{cliente.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-400">Placa</Label>
                  <Input
                    value={formData.placa}
                    onChange={(e) => setFormData({ ...formData, placa: e.target.value.toUpperCase() })}
                    className="bg-obsidian border-white/10 text-white"
                    required
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-400">Cor</Label>
                  <Input
                    value={formData.cor}
                    onChange={(e) => setFormData({ ...formData, cor: e.target.value })}
                    className="bg-obsidian border-white/10 text-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-400">Marca</Label>
                  <Input
                    value={formData.marca}
                    onChange={(e) => setFormData({ ...formData, marca: e.target.value })}
                    className="bg-obsidian border-white/10 text-white"
                    required
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-400">Modelo</Label>
                  <Input
                    value={formData.modelo}
                    onChange={(e) => setFormData({ ...formData, modelo: e.target.value })}
                    className="bg-obsidian border-white/10 text-white"
                    required
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-400">Ano</Label>
                  <Input
                    type="number"
                    value={formData.ano}
                    onChange={(e) => setFormData({ ...formData, ano: e.target.value })}
                    className="bg-obsidian border-white/10 text-white"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <Button type="button" variant="ghost" onClick={() => setIsDialogOpen(false)} className="text-slate-400">
                  Cancelar
                </Button>
                <Button type="submit" className="bg-electric-blue hover:bg-blue-600 text-white" data-testid="submit-veiculo-btn">
                  {editingVeiculo ? 'Atualizar' : 'Cadastrar'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-500 w-5 h-5" />
          <Input
            type="text"
            placeholder="Buscar por placa, marca ou modelo..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-carbon border-white/10 text-white"
            data-testid="search-veiculos-input"
          />
        </div>
      </div>

      <div className="bg-carbon border border-white/5 rounded-md overflow-hidden shadow-lg">
        <table className="w-full">
          <thead className="bg-white/2 border-b border-white/5">
            <tr>
              <th className="text-left px-6 py-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Placa</th>
              <th className="text-left px-6 py-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Veículo</th>
              <th className="text-left px-6 py-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Cliente</th>
              <th className="text-right px-6 py-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Ações</th>
            </tr>
          </thead>
          <tbody>
            {filteredVeiculos.length === 0 ? (
              <tr>
                <td colSpan="4" className="text-center py-12">
                  <Car className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-500">Nenhum veículo cadastrado</p>
                </td>
              </tr>
            ) : (
              filteredVeiculos.map((veiculo) => (
                <tr key={veiculo.id} className="table-row border-b border-white/5" data-testid={`veiculo-row-${veiculo.id}`}>
                  <td className="px-6 py-4 text-sm text-slate-300 font-mono font-bold">{veiculo.placa}</td>
                  <td className="px-6 py-4 text-sm text-slate-300">
                    {veiculo.marca} {veiculo.modelo} {veiculo.ano && `(${veiculo.ano})`}
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-300">{getClienteNome(veiculo.cliente_id)}</td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end space-x-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setHistoricoVeiculo(veiculo)}
                        className="text-blue-400 hover:text-white hover:bg-white/5"
                        data-testid={`historico-veiculo-${veiculo.id}`}
                        title="Ver histórico do veículo"
                      >
                        <History className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleEdit(veiculo)}
                        className="text-electric-blue hover:text-white hover:bg-white/5"
                        data-testid={`edit-veiculo-${veiculo.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(veiculo.id)}
                        className="text-alert-red hover:text-white hover:bg-white/5"
                        data-testid={`delete-veiculo-${veiculo.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <HistoricoVeiculoModal
        open={!!historicoVeiculo}
        onOpenChange={(o) => !o && setHistoricoVeiculo(null)}
        veiculo={historicoVeiculo}
        apiClient={api}
        mode="admin"
        basePathListar="/veiculos/{id}/historico"
        basePathCrud="/historico"
      />
    </div>
  );
}

export default Veiculos;